import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Users, BookOpen, ShieldOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar } from '../../../mascot';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import type { Classroom } from '../../../accounts';
interface CoTeacherWelcomeModalProps {
  open: boolean;
  classroom: Classroom | null;
  onClose: () => void;
}
/**
 * One-time welcome shown after a teacher accepts a co-teacher invite.
 *
 * Djay-zone: YES (Zone 1 — first-run / celebration).
 * Wave 3.3 — Co-teacher accept flow.
 */
export function CoTeacherWelcomeModal({
  open,
  classroom,
  onClose
}: CoTeacherWelcomeModalProps) {
  useEscapeKey(onClose, open && classroom !== null);
  return <AnimatePresence data-id="element-8045">
      {open && classroom && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="co-teacher-welcome-title" data-id="element-8046">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-8047" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-8048">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-indigo-400 to-amber-400" data-id="element-8049" />

            <div className="p-8 text-center" data-id="element-8050">
              <div className="flex justify-center mb-4" data-id="element-8051">
                <div className="relative" data-id="element-8052">
                  <DjayAvatar size="md" emotion="celebrating" data-id="element-8053" />
                  <Sparkles className="absolute -top-1 -right-2 w-5 h-5 text-amber-400" data-id="element-8054" />
                </div>
              </div>

              <h2 id="co-teacher-welcome-title" className="text-2xl font-black text-slate-900 mb-2" data-id="element-8055">
                Welkom in {classroom.name}!
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6" data-id="element-8056">
                Je bent toegevoegd als{' '}
                <span className="font-bold" data-id="element-8057">co-docent</span>. Hieronder zie je
                wat je kunt doen.
              </p>

              <ul className="text-left space-y-3 mb-6" data-id="element-8058">
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-8059">
                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-8060" />
                  <div data-id="element-8061">
                    <p className="text-sm font-bold text-slate-900" data-id="element-8062">
                      Leerlingen begeleiden
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-8063">
                      Je ziet alle leerlingen, hun voortgang en kunt leerlingen
                      toevoegen of verwijderen.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-8064">
                  <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-8065" />
                  <div data-id="element-8066">
                    <p className="text-sm font-bold text-slate-900" data-id="element-8067">
                      Huiswerk &amp; toetsen
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-8068">
                      Je kunt zelfstandig huiswerk en toetsen klaarzetten voor
                      deze klas.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-8069">
                  <ShieldOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8070" />
                  <div data-id="element-8071">
                    <p className="text-sm font-bold text-amber-900" data-id="element-8072">
                      Klas-structuur blijft bij eigenaar
                    </p>
                    <p className="text-xs text-amber-800" data-id="element-8073">
                      Alleen de klas-eigenaar kan de klas hernoemen, verwijderen
                      of andere co-docenten beheren.
                    </p>
                  </div>
                </li>
              </ul>

              <Button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all" data-id="element-8074">
                Aan de slag
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}